import { Analytics } from '@easy-expense/analytics-client';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './partner.css';

/**
 * Partnership landing page (EZ-4616).
 *
 * EE-branded marketing landing reached via the public `/partner` route.
 * Reproduces the partner "coupon card" design's branding/layout, but with
 * neutral copy — there are no discount codes, no "% off", and no prices.
 * The partner chip simply reads "<Partner> member". All CTAs route to
 * `/signup` — there is no login / get-started nav (EZ-4697), so every visitor
 * is funneled into the tracked signup flow.
 *
 * The partner name is read from the `?partnerName=` query param (e.g.
 * `/partner?partnerName=Acme`) and used in the chip copy and forwarded to
 * signup. There is no default — when the param is absent the partner chip is
 * hidden and signup receives an empty `partner` value.
 */

const Stars: React.FC<{ size?: number; rating?: number }> = ({ size = 16, rating = 5 }) => (
  <span className="pl-stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={i < rating ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={i < rating ? 0 : 1.5}
        aria-hidden="true"
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </span>
);

const TESTIMONIALS: {
  initials: string;
  color: string;
  name: string;
  role: string;
  rating: number;
  time: string;
  text: string;
}[] = [
  {
    initials: 'M',
    color: 'linear-gradient(135deg,#1369E3,#0d4fa8)',
    name: 'Marcus R.',
    role: 'HVAC contractor',
    rating: 5,
    time: '3 weeks ago',
    text: "Used to just shove receipts in the glovebox and deal with it in April. Now I just snap a pic at the counter. Found like $1,900 in deductions I would've just lost.",
  },
  {
    initials: 'D',
    color: 'linear-gradient(135deg,#14A05D,#0c6b3e)',
    name: 'Dana K.',
    role: 'Electrical foreman',
    rating: 5,
    time: '1 month ago',
    text: "Got the whole crew using it, took maybe a day to get everyone on board. End of month reports used to eat my whole Sunday, now it's like ten minutes.",
  },
  {
    initials: 'T',
    color: 'linear-gradient(135deg,#786CE5,#5a4fce)',
    name: "Tony's Plumbing",
    role: 'Verified member',
    rating: 4,
    time: '2 months ago',
    text: 'Honestly impressed it can read the crumpled up receipts that live in the bottom of my truck.',
  },
];

export const PartnerLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerName = searchParams.get('partnerName')?.trim() || '';

  const goSignup = () => {
    Analytics.track('partnership_landing_page_clicked');
    navigate(`/signup?partner=${encodeURIComponent(partnerName)}`);
  };

  return (
    <div className="pl-stage">
      <div className="pl-canvas">
        <header className="pl-header">
          <div className="pl-lockup">
            <img
              className="pl-logo"
              src={require('../images/partner/EasyExpense_Logo.webp')}
              alt="Easy Expense"
            />
          </div>
        </header>

        <div className="pl-card">
          <div className="pl-left">
            {partnerName && (
              <div className="pl-chip">
                <span className="pl-partner">{partnerName}</span>
                <span className="pl-label">member</span>
              </div>
            )}

            <h1>Receipt tracking that does the work for you.</h1>
            <p className="pl-sub">
              Snap a photo of any receipt and Easy Expense handles the rest. Automatically
              categorized, stored, and ready for tax time.{' '}
              <b>Peace of mind for you and the whole crew.</b>
            </p>

            <div className="pl-cta-row">
              <button type="button" className="pl-try" onClick={goSignup}>
                Claim your 25% discount
                <svg
                  className="pl-arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              </button>
              <span className="pl-reassure">
                Exclusive member pricing · Free trial included, no card required
              </span>
            </div>

            <div className="pl-divider" />

            <div className="pl-ratings">
              <div className="pl-rating">
                <img
                  className="pl-store-ic"
                  src={require('../images/partner/GooglePlay_Icon.svg')}
                  alt="Google Play"
                />
                <div className="pl-col">
                  <div className="pl-score-row">
                    <span className="pl-score">4.8</span>
                    <Stars size={14} />
                  </div>
                  <span className="pl-meta">Google Play · 19k reviews</span>
                </div>
              </div>
              <div className="pl-rating-sep" />
              <div className="pl-rating">
                <img
                  className="pl-store-ic"
                  src={require('../images/partner/AppleStore_Icon.svg')}
                  alt="App Store"
                />
                <div className="pl-col">
                  <div className="pl-score-row">
                    <span className="pl-score">4.9</span>
                    <Stars size={14} />
                  </div>
                  <span className="pl-meta">App Store · 2.4k reviews</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pl-visual">
            <span className="pl-dot a" />
            <span className="pl-dot b" />
            <img
              className="pl-phone"
              src={require('../images/partner/receipt-scanner.gif')}
              alt="Easy Expense receipt scanner in action"
            />
          </div>
        </div>

        <div className="pl-testimonials">
          {TESTIMONIALS.map((q, i) => (
            <figure className="pl-quote" key={i}>
              <Stars size={15} rating={q.rating} />
              <blockquote>“{q.text}”</blockquote>
              <figcaption>
                <span className="pl-av" style={{ background: q.color }}>
                  {q.initials}
                </span>
                <span className="pl-who">
                  <span className="pl-name">{q.name}</span>
                  <span className="pl-role">
                    {q.role}
                    {' · '}
                    {q.time}
                  </span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
};
